import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { OrderEvent, EventGroup } from "@/context/EventsContext";
import { formatCurrency } from "@/utils/formatCurrency";
import { ArrowUpCircle, ArrowDownCircle, ChevronDown, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ORDER_TYPE_LABELS: Record<number, string> = {
  0: "BUY",
  1: "SELL",
  2: "BUY LIMIT",
  3: "SELL LIMIT",
  4: "BUY STOP",
  5: "SELL STOP",
};

// An event is "new" if it happened within the last hour
const NEW_EVENT_WINDOW_MS = 60 * 60 * 1000;

export const isNewEvent = (isoString: string): boolean => {
  const diffMs = Date.now() - new Date(isoString).getTime();
  return diffMs < NEW_EVENT_WINDOW_MS;
};

export const formatEventTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const getNetProfit = (event: OrderEvent): number =>
  (event.payload.profit ?? 0) + (event.payload.commission ?? 0) + (event.payload.swap ?? 0);

const NewIndicator = () => (
  <span
    className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse"
    title="New"
    aria-label="New"
  />
);

const TimeWithIndicator = ({ isoString }: { isoString: string }) => {
  const isNew = isNewEvent(isoString);
  return (
    <div className="flex items-center gap-1.5 justify-end">
      {isNew && <NewIndicator />}
      <p className="text-xs text-muted-foreground">{formatEventTime(isoString)}</p>
    </div>
  );
};

const SingleEventRow = ({ event, indent }: { event: OrderEvent; indent?: boolean }) => {
  const { payload, type, source } = event;
  const typeLabel = ORDER_TYPE_LABELS[payload.type] ?? "ORDER";
  const netProfit = getNetProfit(event);
  const isOpened = type === "order_opened";
  const isImport = source === "import";

  return (
    <div className={`flex items-center gap-4 py-3 ${indent ? "pl-12" : ""} ${isImport ? "opacity-60" : ""}`}>
      {!indent && (
        <div className="shrink-0">
          {isOpened ? (
            <ArrowUpCircle className="h-7 w-7 text-blue-500" />
          ) : (
            <ArrowDownCircle className={`h-7 w-7 ${netProfit >= 0 ? "text-green-500" : "text-red-500"}`} />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{typeLabel}</span>
          <span className="text-xs text-muted-foreground">{payload.lots} lots</span>
          <span className="text-xs text-muted-foreground">#{payload.ticket}</span>
          {isImport && (
            <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">import</span>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        {!isOpened && (
          <p className={`font-medium text-sm ${netProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
            {formatCurrency(netProfit, { showSign: netProfit > 0 })}
          </p>
        )}
      </div>
    </div>
  );
};

export const EventGroupCard = ({ group }: { group: EventGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const isSingle = group.events.length === 1;
  const isOpened = group.eventType === "order_opened";
  const count = group.events.length;
  const actionLabel = isOpened ? "Opened" : "Closed";
  const profitColor = group.totalProfit >= 0 ? "text-green-600" : "text-red-600";
  const iconColor = isOpened
    ? "text-blue-500"
    : group.totalProfit >= 0
      ? "text-green-500"
      : "text-red-500";

  if (isSingle) {
    const event = group.events[0];
    const typeLabel = ORDER_TYPE_LABELS[event.payload.type] ?? "ORDER";

    return (
      <Card>
        <CardContent className="flex items-center gap-4 py-4">
          <div className="shrink-0">
            {isOpened ? (
              <ArrowUpCircle className={`h-8 w-8 ${iconColor}`} />
            ) : (
              <ArrowDownCircle className={`h-8 w-8 ${iconColor}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{group.symbol}</span>
              <span className="text-xs text-muted-foreground">{typeLabel}</span>
              <span className="text-xs text-muted-foreground">{event.payload.lots} lots</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {actionLabel} · Ticket #{event.payload.ticket}
            </p>
          </div>
          <div className="text-right shrink-0">
            {!isOpened && (
              <p className={`font-semibold text-sm ${profitColor}`}>
                {formatCurrency(group.totalProfit, { showSign: group.totalProfit > 0 })}
              </p>
            )}
            <TimeWithIndicator isoString={group.latestTime} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-0 px-0">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-4 py-4 px-6 w-full text-left hover:bg-accent/30 transition-colors rounded-lg cursor-pointer"
        >
          <div className="shrink-0">
            {isOpened ? (
              <ArrowUpCircle className={`h-8 w-8 ${iconColor}`} />
            ) : (
              <ArrowDownCircle className={`h-8 w-8 ${iconColor}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{group.symbol}</span>
              <span className="text-xs text-muted-foreground">
                {actionLabel} {count} orders
              </span>
            </div>
          </div>
          <div className="text-right shrink-0 flex items-center gap-3">
            <div>
              {!isOpened && (
                <p className={`font-semibold text-sm ${profitColor}`}>
                  {formatCurrency(group.totalProfit, { showSign: group.totalProfit > 0 })}
                </p>
              )}
              <TimeWithIndicator isoString={group.latestTime} />
            </div>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="border-t px-6 pb-2">
                {group.events.map((event) => (
                  <SingleEventRow key={event.id} event={event} indent />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};
